/**
 * Pure UI-gating helper for the comparator affordances on
 * `<OrderInProgress>` — the Add Comparator button and the Mother/Father
 * comparator parent edit/remove pencils (GEN-2998).
 *
 * Rule (strategy-aware editable window):
 *   Affordances are available IFF BOTH of the following hold:
 *     1) A current test-type config is loaded (`hasSelectedTestTypeConfig`).
 *     2) The order is in an editable status for its fulfillment strategy:
 *        - rcigm: ALLOWLIST of `ORDER_PLACED` (pending) / `ORDER_SUBMITTED`
 *          (submitted) — frozen at accession.
 *        - inflection_test: additionally `SAMPLE_RECEIVED` (accessioned) and
 *          `RESULT_READY` (complete); only `CANCELLED` is terminal/blocked.
 *
 * Everything else returns false, including `CANCELLED` (terminal), null/undefined
 * status, and any future/unknown status value. Using an allowlist (not a denylist
 * of terminal statuses) is safe-by-default: an as-yet-unmodeled non-editable
 * status is blocked rather than accidentally shown, avoiding a false affordance
 * the server would reject with HTTP 409.
 *
 * The inflection_test relaxation mirrors the authoritative provider-api gate
 * (`_assert_order_payload_mutable_for_provider_update`), which only rejects
 * `cancelled` for inflection_test while still rejecting accessioned/complete for
 * rcigm.
 *
 * This is UI gating only. The provider-api enforces the authoritative
 * server-side editability check on every comparator mutation (rejecting
 * accessioned/complete/cancelled with 409); this helper exists purely to
 * control affordance visibility in the SPA and mirror that boundary.
 *
 * Kept as a pure function so it can be unit-tested without mounting the
 * ~2,700-LOC `<OrderInProgress>` page.
 */

import type { FulfillmentStrategy } from '@/config/uiConfig'
import { OrderStatusDisplay } from '@/types/NavigationTypes'

export interface CanEditComparatorParentsInput {
  orderStatus?: OrderStatusDisplay | null
  hasSelectedTestTypeConfig: boolean
  /**
   * Order fulfillment strategy. For ``inflection_test`` the editable window is
   * wider (see ``INFLECTION_EDITABLE_STATUSES``): the order stays editable
   * through accessioning and after the final report; only ``CANCELLED`` is
   * terminal. When omitted or ``rcigm``, the stricter pre-accession allowlist
   * applies (unchanged rcigm behavior).
   */
  fulfillmentStrategy?: FulfillmentStrategy
}

// Pre-accession statuses where comparator edits are permitted for rcigm.
// Allowlist (not a denylist) so unknown/future statuses are blocked by default.
const EDITABLE_STATUSES: ReadonlySet<OrderStatusDisplay> = new Set([
  OrderStatusDisplay.ORDER_PLACED,
  OrderStatusDisplay.ORDER_SUBMITTED,
])

// inflection_test stays editable through accessioning (SAMPLE_RECEIVED) and
// after the final report (RESULT_READY). Comparator ADDs are still separately
// bounded by the 7-day post-final-report window (`canAddComparators`) and
// existing-member HPO edits by the SeqOne HPO lock — those layer on top. Only
// CANCELLED is terminal (excluded here) and blocked, mirroring the authoritative
// provider-api gate.
const INFLECTION_EDITABLE_STATUSES: ReadonlySet<OrderStatusDisplay> = new Set([
  OrderStatusDisplay.ORDER_PLACED,
  OrderStatusDisplay.ORDER_SUBMITTED,
  OrderStatusDisplay.SAMPLE_RECEIVED,
  OrderStatusDisplay.RESULT_READY,
])

export function canEditComparatorParents(input: CanEditComparatorParentsInput): boolean {
  if (!input.hasSelectedTestTypeConfig) return false
  if (input.orderStatus == null) return false
  const allow = input.fulfillmentStrategy === 'inflection_test' ? INFLECTION_EDITABLE_STATUSES : EDITABLE_STATUSES
  return allow.has(input.orderStatus)
}
